import { getApiClient } from "./apiClient";

export interface Portfolio {
  id: string;
  name: string;
  description?: string;
  created_by: string;
  created_by_username: string;
  users: PortfolioUser[];
  entity_counts: Record<string, number>;
  user_permission: "OWNER" | "EDITOR" | "VIEWER" | null;
  created_at: string;
  updated_at: string;
}

export interface PortfolioUser {
  id: string;
  user: string;
  username: string;
  email: string;
  permission_level: "OWNER" | "EDITOR" | "VIEWER";
  created_at: string;
}

export interface PortfolioItem {
  id: string;
  portfolio: string;
  entity_type: "organization" | "facility" | "project" | "source";
  entity_id: string;
  notes?: string;
  labels: string[];
  pin_rank: number;
  added_by: string;
  added_by_username: string;
  created_at: string;
  updated_at: string;
}

export interface PortfolioSummary {
  counts: Record<string, number>;
}

export interface PortfolioPreferences {
  id: string;
  portfolio: string;
  user: string;
  tab_configs: Record<string, any>;
  created_at: string;
  updated_at: string;
}

export interface CreatePortfolioRequest {
  name: string;
  description?: string;
}

export interface UpdatePortfolioRequest {
  name?: string;
  description?: string;
}

export interface CreatePortfolioItemRequest {
  entity_type: "organization" | "facility" | "project" | "source";
  entity_id: string;
  notes?: string;
  labels?: string[];
  pin_rank?: number;
}

export interface CreatePortfolioItemResponse {
  status: "created" | "already_exists";
  message?: string;
  item: PortfolioItem;
}

export interface BulkAddItemsRequest {
  items: CreatePortfolioItemRequest[];
}

export interface BulkDeleteItemsRequest {
  item_ids: number[];
}

export interface BulkUpdateItemsRequest {
  item_ids: number[];
  updates: {
    notes?: string;
    labels?: string[];
    pin_rank?: number;
  };
}

export interface PortfolioItemsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: PortfolioItem[];
}

export interface PortfolioListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Portfolio[];
}

export interface PortfolioItemFilters {
  entity_type?: string;
  search?: string;
  labels?: string;
  added_by?: string;
  ordering?: string;
  page?: number;
  limit?: number;
}

export interface SharePortfolioRequest {
  user_permissions: Array<{
    user_id: string;
    permission_level: "OWNER" | "EDITOR" | "VIEWER";
  }>;
}

class PortfolioApiService {
  // Portfolio CRUD operations
  async getPortfolios(params?: {
    search?: string;
    page?: number;
  }): Promise<PortfolioListResponse> {
    const client = await getApiClient();
    const response = await client.get("/portfolios/", { params });
    return response.data;
  }

  async getPortfolio(portfolioId: string): Promise<Portfolio> {
    const client = await getApiClient();
    const response = await client.get(`/portfolios/${portfolioId}/`);
    return response.data;
  }

  async createPortfolio(data: CreatePortfolioRequest): Promise<Portfolio> {
    const client = await getApiClient();
    const response = await client.post("/portfolios/", data);
    return response.data;
  }

  async updatePortfolio(
    portfolioId: string,
    data: UpdatePortfolioRequest,
  ): Promise<Portfolio> {
    const client = await getApiClient();
    const response = await client.patch(`/portfolios/${portfolioId}/`, data);
    return response.data;
  }

  async deletePortfolio(portfolioId: string): Promise<void> {
    const client = await getApiClient();
    await client.delete(`/portfolios/${portfolioId}/`);
  }

  // Portfolio summary and analytics
  async getPortfolioSummary(portfolioId: string): Promise<PortfolioSummary> {
    const client = await getApiClient();
    const response = await client.get(`/portfolios/${portfolioId}/summary/`);
    return response.data;
  }

  // Portfolio sharing
  async sharePortfolio(
    portfolioId: string,
    data: SharePortfolioRequest,
  ): Promise<void> {
    const client = await getApiClient();
    await client.patch(`/portfolios/${portfolioId}/share/`, data);
  }

  // Portfolio preferences
  async getPortfolioPreferences(
    portfolioId: string,
  ): Promise<PortfolioPreferences> {
    const client = await getApiClient();
    const response = await client.get(`/portfolios/${portfolioId}/prefs/`);
    return response.data;
  }

  async updatePortfolioPreferences(
    portfolioId: string,
    data: { tab_configs: Record<string, any> },
  ): Promise<PortfolioPreferences> {
    const client = await getApiClient();
    const response = await client.patch(
      `/portfolios/${portfolioId}/prefs/`,
      data,
    );
    return response.data;
  }

  // Portfolio items CRUD
  async getPortfolioItems(
    portfolioId: string,
    filters?: PortfolioItemFilters,
  ): Promise<PortfolioItemsResponse> {
    const client = await getApiClient();
    const response = await client.get(`/portfolios/${portfolioId}/items/`, {
      params: filters,
    });
    return response.data;
  }

  async createPortfolioItem(
    portfolioId: string,
    data: CreatePortfolioItemRequest,
  ): Promise<CreatePortfolioItemResponse> {
    const client = await getApiClient();
    const response = await client.post(
      `/portfolios/${portfolioId}/items/`,
      data,
    );
    return response.data;
  }

  async updatePortfolioItem(
    portfolioId: string,
    itemId: string,
    data: Partial<CreatePortfolioItemRequest>,
  ): Promise<PortfolioItem> {
    const client = await getApiClient();
    const response = await client.patch(
      `/portfolios/${portfolioId}/items/${itemId}/`,
      data,
    );
    return response.data;
  }

  async deletePortfolioItem(
    portfolioId: string,
    itemId: string,
  ): Promise<void> {
    const client = await getApiClient();
    await client.delete(`/portfolios/${portfolioId}/items/${itemId}/`);
  }

  // Bulk operations
  async bulkAddItems(
    portfolioId: string,
    data: BulkAddItemsRequest,
  ): Promise<{
    status: string;
    created_count: number;
    total_requested: number;
  }> {
    const client = await getApiClient();
    const response = await client.post(
      `/portfolios/${portfolioId}/items/bulk_add/`,
      data,
    );
    return response.data;
  }

  async bulkDeleteItems(
    portfolioId: string,
    data: BulkDeleteItemsRequest,
  ): Promise<{
    status: string;
    deleted_count: number;
  }> {
    const client = await getApiClient();
    const response = await client.post(
      `/portfolios/${portfolioId}/items/bulk_delete/`,
      data,
    );
    return response.data;
  }

  async bulkUpdateItems(
    portfolioId: string,
    data: BulkUpdateItemsRequest,
  ): Promise<{
    status: string;
    updated_count: number;
  }> {
    const client = await getApiClient();
    const response = await client.post(
      `/portfolios/${portfolioId}/items/bulk_update/`,
      data,
    );
    return response.data;
  }

  // Hydrate portfolio items with fresh entity data
  async hydratePortfolioItems(
    portfolioId: string,
    entityType: "organization" | "facility" | "project" | "source",
  ): Promise<any[]> {
    // First, get all entity IDs for the specified type from portfolio items
    const items = await this.getPortfolioItems(portfolioId, {
      entity_type: entityType,
      limit: 1000, // Get all items
    });

    if (items.results.length === 0) {
      return [];
    }

    const entityIds = items.results.map((item) => item.entity_id);
    return this.hydrateSpecificEntities(portfolioId, entityType, entityIds);
  }

  // Hydrate specific entities by their IDs (for selective page hydration)
  async hydrateSpecificEntities(
    portfolioId: string,
    entityType: "organization" | "facility" | "project" | "source",
    entityIds: string[],
  ): Promise<any[]> {
    if (entityIds.length === 0) {
      return [];
    }

    // Convert singular entity type to plural for search endpoint
    const searchEntityType =
      entityType === "facility" ? "facilities" : `${entityType}s`;

    // Use the enhanced search endpoint to get fresh entity data
    const searchParams = new URLSearchParams({
      entity_type: searchEntityType,
      entity_ids: entityIds.join(","),
      limit: "1000",
    });

    const client = await getApiClient();
    const response = await client.get(
      `/graph/search/?${searchParams.toString()}`,
    );
    return response.data.results || [];
  }

  // Refresh entity data (placeholder for future Kuzu integration)
  async refreshPortfolioData(portfolioId: string): Promise<{
    status: string;
    portfolio_id: string;
  }> {
    const client = await getApiClient();
    const response = await client.post(`/portfolios/${portfolioId}/refresh/`);
    return response.data;
  }
}

export const portfolioApi = new PortfolioApiService();
